import { type HistoryState, useNavigate, useRouter } from '@tanstack/react-router';

/**
 * FILA-TASKS-BACK-TO-LIST-CONTEXT-1 — «Back to list» en la página de un item vuelve a la lista de ORIGEN (proyecto, tablero, mis
 * tareas, con sus filtros y su búsqueda), no siempre a `/my` (que para una tarea que no es mía es una lista vacía).
 *
 * El origen viaja en el ESTADO del historial (`backTo`), no en la URL: un enlace compartido no lo lleva (y nadie puede forjarlo
 * desde fuera), y sobrevive a una recarga porque vive en la entrada del historial. Sin origen, se vuelve al proyecto de la clave
 * (`DEMO-12` → `/DEMO`) y solo si la clave no tiene esa forma, a `/my`.
 */
const KEY = 'backTo';

/** El estado del historial que lleva el origen. `HistoryState` de TanStack es una interfaz vacía (sin forma que aumentar desde aquí). */
const stateFor = (backTo: string): HistoryState => ({ [KEY]: backTo }) as unknown as HistoryState;

/** PURA. El origen guardado en un estado de historial, o null si no hay uno válido: una ruta de esta app, nunca otra, ni otra página de item. */
export const originOf = (state: unknown): string | null => {
  const value =
    typeof state === 'object' && state !== null
      ? (state as Record<string, unknown>)[KEY]
      : undefined;
  if (typeof value !== 'string' || value === '' || value.length > 2048) return null;
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return null;
  // tab, salto de línea y retorno: el navegador los QUITA al resolver una URL, así que `/\t/evil.example` acabaría siendo `//evil.example` (T1 de BABAS, O1)
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return null;
  }
  if (value === '/item' || value.startsWith('/item/')) return null;
  return value;
};

/** PURA. La clave de proyecto de una referencia de item (`DEMO-12` → `DEMO`), o null. */
export const projectKeyOf = (ref: string): string | null => /^(.+)-\d+$/.exec(ref)?.[1] ?? null;

/** Abre la página de un item recordando de dónde se viene. */
export const useOpenItemPage = (): ((ref: string) => void) => {
  const navigate = useNavigate();
  const router = useRouter();
  return (ref) => {
    // A return to the list must not restore the narrow panel over that list.
    const origin = new URL(router.state.location.href, 'https://tasks.test');
    origin.searchParams.delete('item');
    void navigate({
      to: '/item/$ref',
      params: { ref },
      state: () => stateFor(`${origin.pathname}${origin.search}${origin.hash}`),
    });
  };
};

/** De un item a otro (un subitem, uno relacionado) sin perder el origen de la lista. */
export const useSwitchItemPage = (): ((ref: string) => void) => {
  const navigate = useNavigate();
  const router = useRouter();
  return (ref) => {
    const origin = originOf(router.state.location.state);
    void navigate({
      to: '/item/$ref',
      params: { ref },
      ...(origin === null ? {} : { state: () => stateFor(origin) }),
    });
  };
};

/** «Back to list»: el origen si lo hay; si no, el proyecto de la clave; si la clave no tiene forma, `/my`. */
export const useBackToList = (ref: string): (() => void) => {
  const navigate = useNavigate();
  const router = useRouter();
  return () => {
    const origin = originOf(router.state.location.state);
    if (origin !== null) {
      router.history.push(origin);
      return;
    }
    const projectKey = projectKeyOf(ref);
    if (projectKey !== null) void navigate({ to: '/$projectKey', params: { projectKey } });
    else void navigate({ to: '/my' });
  };
};
